import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import DocumentationButton from "components/DocumentationButton";
import DocumentationIconButton from "components/DocumentationIconButton";
import DesktopView from "components/Mobile/DesktopView";
import MobileView from "components/Mobile/MobileView";
import SearchInput from "components/SearchInput";
import SortableTable, { SortableTableColumn } from "components/SortableTable";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { EmptystateKeyColored, EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import useURLParams from "hooks/useURLParams";
import { SearchQueryOrderDirection } from "types/generated";
import { fuzzySearch } from "utils/fuzzySearch";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Counter from "ds/components/Counter";

import { showCreateGpgKeyDrawer } from "./CreateGpgKeyDrawer";
import GpgKeyListItem from "./GpgKeyListItem";
import TerraformRegistryViewHeader from "./ViewHeader";
import { COLUMN_ORDER } from "./constants";
import { GET_GPG_KEYS } from "./gql";
import { GetGpgKeysGql } from "./types";

const activeGpgKeysColumns: SortableTableColumn[] = [
  {
    value: "name",
    label: "Name",
  },
  {
    value: "id",
    label: "ID",
  },
  {
    value: "createdBy",
    label: "Created by",
  },
  {
    value: "createdAt",
    label: "Created",
  },
];

const revokedGpgKeysColumns: SortableTableColumn[] = [
  {
    value: "name",
    label: "Name",
  },
  {
    value: "id",
    label: "ID",
  },
  {
    value: "revokedBy",
    label: "Revoked by",
  },
  {
    value: "revokedAt",
    label: "Revoked",
  },
];

function GpgKeys() {
  const { accountName, viewer } = useTypedContext(AccountContext);
  const { sharingModulesBetweenSpaces } = useTypedFlags();
  const showNewNavigation = sharingModulesBetweenSpaces;

  const { error, loading, data } = useQuery<GetGpgKeysGql>(GET_GPG_KEYS);
  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const [activeGpgKeys, revokedGpgKeys] = useMemo(() => {
    if (!data?.gpgKeys.length) {
      return [[], []];
    }

    let filteredGpgKeys = data.gpgKeys;

    if (searchQuery) {
      filteredGpgKeys = fuzzySearch(filteredGpgKeys, searchQuery.trim(), {
        keys: ["description", "name", "id"],
        scoreThreshold: -1000,
      });
    }

    return [
      filteredGpgKeys.filter((gpgKey) => !gpgKey.revokedAt),
      filteredGpgKeys.filter((gpgKey) => gpgKey.revokedAt),
    ];
  }, [data, searchQuery]);

  useTitle(`GPG keys · ${accountName}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const isLoading = loading;
  const isPageEmpty = !isLoading && !error && data?.gpgKeys.length === 0;
  const hasNoQueryResults =
    activeGpgKeys.length === 0 && revokedGpgKeys.length === 0 && searchQuery;

  return (
    <>
      {!showNewNavigation && <TerraformRegistryViewHeader />}
      {showNewNavigation && (
        <ViewHeader firstLevel>
          <ViewHeaderWrapper>
            <ViewHeaderTitle>GPG Keys</ViewHeaderTitle>
            <MobileView>
              {viewer.admin && (
                <Button onPress={showCreateGpgKeyDrawer} variant="primary" disabled={isLoading}>
                  Register GPG key
                </Button>
              )}
            </MobileView>
          </ViewHeaderWrapper>
          <ViewHeaderWrapper>
            <SearchInput
              placeholder="Search by ID, name and description..."
              filtersOrderSettingsKey="gpgKeysFiltersOrder"
              disabled={isLoading}
            />
            <DesktopView>
              <DocumentationIconButton
                href={getDocsUrl("/vendors/terraform/provider-registry#register-a-gpg-key")}
                tooltipText="Go to GPG keys documentation"
              />
              {viewer.admin && (
                <Button onPress={showCreateGpgKeyDrawer} variant="primary" disabled={isLoading}>
                  Register GPG key
                </Button>
              )}
            </DesktopView>
          </ViewHeaderWrapper>
        </ViewHeader>
      )}
      {!showNewNavigation && (
        <PageInfoHeader title="GPG keys">
          <SearchInput
            placeholder="Search by ID, name and description..."
            filtersOrderSettingsKey="gpgKeysFiltersOrder"
            disabled={isLoading}
          />

          <DesktopView>
            {viewer.admin && (
              <Button onPress={showCreateGpgKeyDrawer} variant="primary" disabled={isLoading}>
                Register GPG key
              </Button>
            )}
          </DesktopView>
        </PageInfoHeader>
      )}
      {isLoading && <PageLoading />}

      {!isLoading && isPageEmpty && !hasNoQueryResults && (
        <EmptyState
          title="Register your first GPG key"
          icon={EmptystateKeyColored}
          caption="GPG keys are used to sign provider archives."
        >
          <Box gap="medium">
            <DocumentationButton
              to={getDocsUrl("/vendors/terraform/provider-registry#register-a-gpg-key")}
              label="Documentation"
            />
            {viewer.admin && (
              <Button variant="primary" onPress={showCreateGpgKeyDrawer}>
                Register GPG key
              </Button>
            )}
          </Box>
        </EmptyState>
      )}
      {!isLoading && hasNoQueryResults && (
        <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
      )}
      {!isLoading && !isPageEmpty && (
        <Box __deprecatedGap="1rem" direction="column">
          {activeGpgKeys.length > 0 && (
            <CollapsibleSection
              fullbleed
              title="Active"
              initialIsCollapsed={false}
              ariaLevel={3}
              ariaLabel="Active"
              titleSuffix={<Counter count={activeGpgKeys.length} />}
            >
              <SortableTable
                items={activeGpgKeys}
                renderItems={(sorted) =>
                  sorted.map((item) => <GpgKeyListItem key={item.id} item={item} />)
                }
                columnOrder={COLUMN_ORDER}
                columns={activeGpgKeysColumns}
                initialSortBy="createdAt"
                initialDirection={SearchQueryOrderDirection.Desc}
                ariaLabel="Active"
              />
            </CollapsibleSection>
          )}

          {revokedGpgKeys.length > 0 && (
            <CollapsibleSection
              title="Revoked"
              initialIsCollapsed={false}
              ariaLevel={3}
              ariaLabel="Revoked"
              fullbleed
              titleSuffix={<Counter count={revokedGpgKeys.length} />}
            >
              <SortableTable
                items={revokedGpgKeys}
                renderItems={(sorted) =>
                  sorted.map((item) => <GpgKeyListItem key={item.id} item={item} />)
                }
                columnOrder={COLUMN_ORDER}
                columns={revokedGpgKeysColumns}
                initialSortBy="revokedAt"
                initialDirection={SearchQueryOrderDirection.Desc}
                ariaLabel="Revoked"
              />
            </CollapsibleSection>
          )}
        </Box>
      )}
    </>
  );
}

export default GpgKeys;
